import React from 'react';
import { Box, Container, Typography, Stack, Button } from '@mui/material';
import Grid from '@mui/material/Grid';
import LockIcon from '@mui/icons-material/Lock';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import AutoDeleteIcon from '@mui/icons-material/AutoDelete';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import BlurOnIcon from '@mui/icons-material/BlurOn';
import GavelIcon from '@mui/icons-material/Gavel';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import ShieldIcon from '@mui/icons-material/Shield';
import BadgeIcon from '@mui/icons-material/Badge';
import Header from './Header';
import SEO from './SEO';
import Footer from './Footer';
import { useNavigate } from 'react-router-dom';

const dataItems = [
  { stored: true, item: 'Flagged clips — only the moments that triggered an alert' },
  { stored: true, item: 'Alert metadata — timestamp, type, severity, reviewer notes' },
  { stored: true, item: 'Audit log — who accessed what, and when' },
  { stored: true, item: 'Staff assignment data — which worker was in which room' },
  { stored: true, item: 'Flagged alert snippets only — short clips (seconds to minutes) that triggered an alert. Full footage streams are never transmitted off the facility network.' },
  { stored: true, item: '48-hour footage buffer — all camera footage retained locally for 48 hours for investigation purposes, then automatically and irrecoverably destroyed unless an investigation hold is applied' },
  { stored: false, item: 'Footage older than 48 hours with no active investigation — permanently and irrecoverably destroyed, never archived or recoverable' },
  { stored: false, item: 'Identifiable children\'s faces — blurred before any clip leaves the facility' },
  { stored: false, item: 'Audio recordings — behavioral AI uses pattern signals, not recordings' },
];

const accessLevels = [
  {
    role: 'Facility Owner / Admin',
    accent: '#5ce1e6',
    sees: ['All alerts across all rooms', 'Full incident history and audit trail', 'Team management and access controls', 'System configuration and settings'],
  },
  {
    role: 'Alert Reviewer',
    accent: '#8c52ff',
    sees: ['Incoming alerts assigned to them', 'Flagged clip for the specific alert', 'Classification tools and notes field', 'Pattern context for their assigned cases'],
  },
  {
    role: 'Facility Manager',
    accent: '#ff9f43',
    sees: ['Only alerts explicitly shared with them by a reviewer', 'No access to the alert queue or raw footage', 'Context summary for incidents affecting their area'],
  },
];

const complianceItems = [
  {
    icon: <ShieldIcon sx={{ fontSize: 22, color: '#5ce1e6' }} />,
    title: 'SOC 2 Type II',
    body: 'SMCLA is built to SOC 2 standards — covering security, availability, and confidentiality. Independent third-party audits verify our controls.',
  },
  {
    icon: <GavelIcon sx={{ fontSize: 22, color: '#5ce1e6' }} />,
    title: 'State audio consent compliance',
    body: 'Audio monitoring laws vary by state. SMCLA configures audio analysis per your state\'s one-party or two-party consent requirements during setup. We handle the compliance — you don\'t have to.',
  },
  {
    icon: <LockIcon sx={{ fontSize: 22, color: '#5ce1e6' }} />,
    title: 'GDPR & data privacy',
    body: 'For facilities operating across jurisdictions, SMCLA\'s data minimization architecture is GDPR-compatible by design. We collect only what\'s needed for safety — nothing more.',
  },
  {
    icon: <BadgeIcon sx={{ fontSize: 22, color: '#5ce1e6' }} />,
    title: 'COPPA — children\'s data',
    body: 'SMCLA does not collect or store personally identifiable information about children. Faces are blurred before any clip is transmitted. We are not in the business of building profiles on children.',
  },
  {
    icon: <FactCheckIcon sx={{ fontSize: 22, color: '#5ce1e6' }} />,
    title: 'Licensing board documentation',
    body: 'Every incident alert — whether validated or dismissed — is permanently logged with reference key, timestamps, reviewer notes, and chain-of-custody metadata. Structured for childcare licensing requirements.',
  },
  {
    icon: <VpnKeyIcon sx={{ fontSize: 22, color: '#5ce1e6' }} />,
    title: 'End-to-end encryption',
    body: 'All video analysis, alert transmission, and clip storage is encrypted in transit and at rest. Your facility\'s data never travels or rests in plaintext.',
  },
];

export default function PrivacyPage() {
  const navigate = useNavigate();

  return (
    <Box sx={{ bgcolor: '#0a0a12', minHeight: '100vh' }}>
      <SEO title="Privacy & Security Architecture" description="How SMCLA handles video, audio, and alert data — data minimization, role-based access, SOC 2 compliance, and children's privacy by design." path="/privacy" />
      <Header onBookDemo={() => navigate('/contact')} />

      {/* Hero */}
      <Box sx={{ position: 'relative', py: { xs: 10, md: 14 }, px: 3, textAlign: 'center', overflow: 'hidden' }}>
        <Box sx={{ position: 'absolute', top: '-10%', left: '50%', transform: 'translateX(-50%)', width: 600, height: 600, borderRadius: '50%', background: 'radial-gradient(circle, rgba(92,225,230,0.07) 0%, transparent 70%)', filter: 'blur(60px)', pointerEvents: 'none' }} />
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, bgcolor: 'rgba(92,225,230,0.08)', border: '1px solid rgba(92,225,230,0.2)', borderRadius: 10, px: 2, py: 0.75, mb: 3 }}>
            <LockIcon sx={{ color: '#5ce1e6', fontSize: 14 }} />
            <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 500, fontSize: 12, color: '#5ce1e6', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              Privacy & Security
            </Typography>
          </Box>
          <Typography variant="h1" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '2.2rem', md: '3.8rem' }, letterSpacing: '-0.03em', color: '#f0f0f8', mb: 2.5, lineHeight: 1.05 }}>
            Privacy is not a setting.<br />
            <Box component="span" sx={{ color: '#5ce1e6' }}>It's the architecture.</Box>
          </Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: { xs: 16, md: 20 }, color: '#9499b0', maxWidth: 600, mx: 'auto', lineHeight: 1.65 }}>
            SMCLA was designed from day one around data minimization. We analyze what we need to protect children, and discard everything else. This page explains exactly how.
          </Typography>
        </Container>
      </Box>

      {/* What we store vs. don't */}
      <Box sx={{ bgcolor: '#0d0d18', borderTop: '1px solid rgba(255,255,255,0.06)', borderBottom: '1px solid rgba(255,255,255,0.06)', py: { xs: 8, md: 12 }, px: 3 }}>
        <Container maxWidth="md">
          <Typography variant="overline" sx={{ display: 'block', color: '#5ce1e6', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, letterSpacing: '0.12em', mb: 1.5 }}>
            Data Minimization
          </Typography>
          <Typography variant="h2" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.5rem' }, color: '#f0f0f8', letterSpacing: '-0.02em', mb: 2 }}>
            We store what matters. We discard everything else.
          </Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 15, color: '#9499b0', lineHeight: 1.7, mb: 7, maxWidth: 560 }}>
            SMCLA is not a surveillance archive. Video is analyzed in real time and discarded immediately unless it triggers an alert. The system retains only what is necessary for safety and compliance.
          </Typography>

          <Stack spacing={1.5}>
            {dataItems.map(({ stored, item }) => (
              <Box key={item} sx={{ display: 'flex', gap: 2.5, alignItems: 'center', p: 2.5, bgcolor: '#13131f', border: `1px solid ${stored ? 'rgba(92,225,230,0.1)' : 'rgba(255,255,255,0.04)'}`, borderRadius: 2 }}>
                <Box sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: stored ? 'rgba(92,225,230,0.1)' : 'rgba(255,107,107,0.08)', border: `1px solid ${stored ? 'rgba(92,225,230,0.25)' : 'rgba(255,107,107,0.2)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: 14, color: stored ? '#5ce1e6' : '#ff6b6b', lineHeight: 1 }}>
                    {stored ? '✓' : '✕'}
                  </Typography>
                </Box>
                <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, color: stored ? '#d0d0e0' : '#9499b0', lineHeight: 1.5 }}>
                  {stored ? <strong style={{ color: '#f0f0f8' }}>Stored: </strong> : <strong style={{ color: '#9499b0' }}>Not stored: </strong>}
                  {item}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Container>
      </Box>

      {/* Children's privacy */}
      <Box sx={{ py: { xs: 8, md: 12 }, px: 3 }}>
        <Container maxWidth="md">
          <Typography variant="overline" sx={{ display: 'block', color: '#8c52ff', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, letterSpacing: '0.12em', mb: 1.5 }}>
            Children First
          </Typography>
          <Typography variant="h2" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.5rem' }, color: '#f0f0f8', letterSpacing: '-0.02em', mb: 2 }}>
            Children's privacy is protected by design.
          </Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 15, color: '#9499b0', lineHeight: 1.7, mb: 7, maxWidth: 580 }}>
            Every privacy feature was designed with children in mind first. We see the behavior that matters — and only that.
          </Typography>

          <Grid container spacing={3}>
            {[
              { icon: <BlurOnIcon sx={{ fontSize: 24, color: '#8c52ff' }} />, title: 'Faces blurred before transmission', body: 'Children\'s faces are blurred in every alert clip before it leaves the facility network. Authorized reviewers see the behavioral incident — not an identifiable child.' },
              { icon: <VisibilityOffIcon sx={{ fontSize: 24, color: '#8c52ff' }} />, title: 'No continuous footage retained', body: 'Video streams are analyzed in real time and discarded. If no alert is triggered, the footage is never stored. There is no surveillance archive for anyone to access.' },
              { icon: <AutoDeleteIcon sx={{ fontSize: 24, color: '#8c52ff' }} />, title: 'Auto-deletion after validation', body: 'Alert clips are automatically deleted after the review window closes — unless held for an active investigation or legal proceeding. Nothing lingers.' },
              { icon: <AdminPanelSettingsIcon sx={{ fontSize: 24, color: '#8c52ff' }} />, title: 'No children\'s PII collected', body: 'SMCLA does not collect, store, or process personally identifiable information about children. We are not in the business of building profiles on children.' },
            ].map(({ icon, title, body }) => (
              <Grid key={title} size={{ xs: 12, sm: 6 }}>
                <Box sx={{ p: 3.5, bgcolor: '#13131f', border: '1px solid rgba(140,82,255,0.1)', borderRadius: 3, height: '100%' }}>
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 2 }}>
                    {icon}
                    <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 15, color: '#f0f0f8' }}>{title}</Typography>
                  </Box>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, color: '#9499b0', lineHeight: 1.7 }}>{body}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Access controls */}
      <Box sx={{ bgcolor: '#0d0d18', borderTop: '1px solid rgba(255,255,255,0.06)', borderBottom: '1px solid rgba(255,255,255,0.06)', py: { xs: 8, md: 12 }, px: 3 }}>
        <Container maxWidth="md">
          <Typography variant="overline" sx={{ display: 'block', color: '#5ce1e6', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, letterSpacing: '0.12em', mb: 1.5 }}>
            Access Controls
          </Typography>
          <Typography variant="h2" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.5rem' }, color: '#f0f0f8', letterSpacing: '-0.02em', mb: 2 }}>
            The right person sees the right information.<br />Nothing more.
          </Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 15, color: '#9499b0', lineHeight: 1.7, mb: 7, maxWidth: 560 }}>
            SMCLA's role-based access model mirrors the org structure of real childcare operations. Every access event is logged — who viewed what, when, and why.
          </Typography>

          <Stack spacing={2.5} sx={{ mb: 5 }}>
            {accessLevels.map(({ role, accent, sees }) => (
              <Box key={role} sx={{ p: { xs: 3, md: 4 }, bgcolor: '#13131f', border: `1px solid ${accent}15`, borderLeft: `3px solid ${accent}`, borderRadius: 3 }}>
                <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 15, color: accent, mb: 2 }}>
                  {role}
                </Typography>
                <Stack spacing={0.75}>
                  {sees.map((s, i) => (
                    <Box key={i} sx={{ display: 'flex', gap: 1.5 }}>
                      <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: accent, mt: 0.8, flexShrink: 0 }} />
                      <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0', lineHeight: 1.6 }}>{s}</Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            ))}
          </Stack>

          <Box sx={{ p: { xs: 3, md: 4 }, bgcolor: '#13131f', border: '1px solid rgba(92,225,230,0.15)', borderRadius: 3, borderLeft: '3px solid #5ce1e6' }}>
            <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 14, color: '#5ce1e6', mb: 1 }}>
              Every access is logged
            </Typography>
            <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, color: '#9499b0', lineHeight: 1.7 }}>
              Every time a reviewer opens a clip, every time an owner accesses the audit trail, every time a role assignment changes — it is logged with a timestamp and user identity. Chain-of-custody metadata holds up in licensing board reviews and legal proceedings.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Staff dignity */}
      <Box sx={{ py: { xs: 8, md: 12 }, px: 3 }}>
        <Container maxWidth="md">
          <Typography variant="overline" sx={{ display: 'block', color: '#ff9f43', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, letterSpacing: '0.12em', mb: 1.5 }}>
            Staff Protection
          </Typography>
          <Typography variant="h2" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.5rem' }, color: '#f0f0f8', letterSpacing: '-0.02em', mb: 2 }}>
            Privacy protects good staff too.
          </Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 15, color: '#9499b0', lineHeight: 1.7, mb: 5, maxWidth: 580 }}>
            False accusations are common in childcare. SMCLA's tamper-evident, timestamped evidence resolves disputes immediately — before a career is destroyed and before a lawsuit is filed.
          </Typography>
          <Grid container spacing={3}>
            {[
              { title: 'No bulk surveillance of staff', body: 'Staff are not monitored continuously. SMCLA watches for behavioral anomalies — not for general performance tracking or time-and-motion analysis. Workers are not under a microscope.' },
              { title: 'Exoneration is instant', body: 'When a false accusation is made, the timestamped clip evidence from the relevant time window is retrieved immediately. Good staff are cleared before an HR process begins.' },
              { title: 'Dismissed alerts are documented, not deleted', body: 'If an alert is reviewed and dismissed as a false alarm, that dismissal is logged permanently. A pattern of false alerts can itself become evidence — in either direction.' },
              { title: 'Workers are not identified in clips', body: 'Staff are not tagged by name in clips shared with reviewers. Behavioral analysis focuses on the action, not the individual, until a reviewer escalates for investigation.' },
            ].map(({ title, body }) => (
              <Grid key={title} size={{ xs: 12, sm: 6 }}>
                <Box sx={{ p: 3, bgcolor: '#13131f', border: '1px solid rgba(255,159,67,0.08)', borderRadius: 3, height: '100%' }}>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 14, color: '#ff9f43', mb: 1 }}>{title}</Typography>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0', lineHeight: 1.7 }}>{body}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Compliance */}
      <Box sx={{ bgcolor: '#0d0d18', borderTop: '1px solid rgba(255,255,255,0.06)', borderBottom: '1px solid rgba(255,255,255,0.06)', py: { xs: 8, md: 12 }, px: 3 }}>
        <Container maxWidth="md">
          <Typography variant="overline" sx={{ display: 'block', color: '#5ce1e6', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, letterSpacing: '0.12em', mb: 1.5 }}>
            Compliance Coverage
          </Typography>
          <Typography variant="h2" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.5rem' }, color: '#f0f0f8', letterSpacing: '-0.02em', mb: 2 }}>
            Built for the regulatory environment<br />childcare actually operates in.
          </Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 15, color: '#9499b0', lineHeight: 1.7, mb: 7, maxWidth: 580 }}>
            Childcare is one of the most regulated industries in the US. SMCLA was built to operate inside that environment — not around it.
          </Typography>

          <Grid container spacing={3}>
            {complianceItems.map(({ icon, title, body }) => (
              <Grid key={title} size={{ xs: 12, sm: 6 }}>
                <Box sx={{ p: 3.5, bgcolor: '#13131f', border: '1px solid rgba(92,225,230,0.08)', borderRadius: 3, height: '100%', transition: 'border-color 0.2s', '&:hover': { borderColor: 'rgba(92,225,230,0.2)' } }}>
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 2 }}>
                    {icon}
                    <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 15, color: '#f0f0f8' }}>{title}</Typography>
                  </Box>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, color: '#9499b0', lineHeight: 1.7 }}>{body}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA */}
      <Box sx={{ py: { xs: 8, md: 12 }, px: 3, textAlign: 'center' }}>
        <Container maxWidth="sm">
          <Typography variant="h2" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.5rem' }, color: '#f0f0f8', letterSpacing: '-0.02em', mb: 2 }}>
            Questions about compliance?
          </Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 17, color: '#9499b0', lineHeight: 1.7, mb: 5 }}>
            Our team includes people who have worked through SOC 2, GDPR, and state childcare licensing requirements. We're happy to walk through any compliance question before you make a decision.
          </Typography>
          <Button variant="contained" size="large" onClick={() => navigate('/contact')} sx={{ px: 5, py: 1.75, fontWeight: 700, fontSize: 16, bgcolor: '#5ce1e6', color: '#0a0a12', borderRadius: 2, boxShadow: '0 0 28px rgba(92,225,230,0.2)', '&:hover': { bgcolor: '#4ecfd4' } }}>
            Talk to Us →
          </Button>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
}
